import dayjs from 'dayjs';
import { roundAndDropTrailingZeros } from './roundAndDropTrailingZeros';
import {
  IBaselineAverageItem,
  IBaselineResult,
  EPillarType,
  IStudentBaselineGlobalResult,
  IBaselineSkillAverages,
} from '../models';

export interface ConvertedData {
  data: {
    blurb?: string;
    score: number;
  };
  title: string;
}

export function getSkillFeedbackInfo(el: EPillarType) {
  let skillFeedbackText;
  let chartDescription;
  let aspiration;

  switch (el) {
    case EPillarType.ACTIVE_CITIZENSHIP:
      aspiration = 'Active Citizenship';
      skillFeedbackText =
        'This pillar encompasses the skills of respect, empathy, participation and cultural awareness. A lot of your underlying attributes can determine how you fare with these skills. Read more below to understand your score.';
      chartDescription =
        'These scores are tracking if you’re sensitive, accept people as they are and whether you take care of when to say what in a given situation.';
      break;
    case EPillarType.LEARNING:
      aspiration = 'Learning';
      skillFeedbackText =
        'This pillar encompasses the skills of creativity, critical thinking, problem solving, divergent thinking, research and metacognition. A lot of your underlying attributes can determine how you fare with these skills. Read more below to understand your score.';
      chartDescription =
        'These scores are tracking how organized and systematic you are in your learning and whether you’re able to examine your ideas objectively.';
      break;
    case EPillarType.COMMUNICATION:
      aspiration = 'Communication';
      skillFeedbackText =
        'This pillar encompasses the skills of verbal and non-verbal communication, visual communication, written communications and presentations. A lot of your underlying attributes can determine how you fare with these skills. Read more below to understand your score.';
      chartDescription =
        'Here we’re looking at the underlying attributes of communication such as clarity, understanding the context and taking into consideration others’ feelings.';
      break;
    case EPillarType.PERSONAL_DEVELOPMENT:
      aspiration = 'Personal Development';
      skillFeedbackText =
        'This pillar encompasses the skills of having a positive mindset, resilience, self-development, personal branding and basic financial literacy. A lot of your underlying attributes can determine how you fare with these skills. Read more below to understand your score.';
      chartDescription =
        'The Personal Development pillar focuses on skills related to your mental wellbeing. The stats here indicate where you are given your underlying behavioral attributes.';
      break;
    case EPillarType.CAREER_READINESS:
      aspiration = 'Career Readiness';
      skillFeedbackText =
        'This pillar encompasses the skills of cooperation, negotiation, working effectively, and networking. A lot of your underlying attributes can determine how you fare with these skills. Read more below to understand your score.';
      chartDescription =
        'The employability pillar focuses on underlying attributes such as understanding the potential of others, ability to influence people and more.';
      break;
    default:
      aspiration = '';
      skillFeedbackText = '';
      chartDescription = '';
  }

  return { aspiration, skillFeedbackText, chartDescription };
}

export const getBaselineAssessmentUtilites = ({
  results,
  averages,
  globalAverages,
  globalResults,
}: {
  results?: IBaselineResult[];
  averages?: IBaselineAverageItem[];
  globalResults?: IStudentBaselineGlobalResult;
  globalAverages?: IBaselineSkillAverages;
}) => {
  const convertToArray = (obj: {
    [key: string]: { blurb: string; score: number };
  }): ConvertedData[] => {
    if (results && results.length > 0) {
      return Object.entries(obj).map((e) => ({ data: e[1], title: e[0] }));
    }

    return [];
  };

  return {
    convertToArray,
    generateAverage: (index: number): ConvertedData[] => {
      if (averages && averages.length >= index + 1) {
        return Object.entries(averages[index].averages).map((e) => ({
          title: e[0],
          data: {
            score: roundAndDropTrailingZeros(e[1]),
            blurb: `Your score is ${roundAndDropTrailingZeros(e[1])}%`,
          },
        }));
      }
      return [];
    },
    generateGlobalAverage: () => {
      if (globalAverages)
        return Object.entries(globalAverages).map((e) => ({
          title: e[0],
          data: {
            score: roundAndDropTrailingZeros(e[1]),
            blurb: `Global score is ${roundAndDropTrailingZeros(e[1])}%`,
          },
        }));

      return [];
    },
    getSkillFeedbackInfo,

    //Generating labels for chart
    generateLabels: (index: number) => {
      if (results && results.length >= index + 1) {
        return `Attempt ${results[index]?.['attempt']} (${dayjs(
          results[index].createdAt
        ).format('DD/MM/YYYY')})`;
      }
      return null;
    },

    //Generating data for chart
    generateData: (index: number, el: EPillarType): ConvertedData[] => {
      if (results && results.length >= index + 1) {
        if (
          results[index] &&
          Object.keys(results[index]).length > 0 &&
          'data' in results[index] &&
          el in results[index].data
        )
          return convertToArray(results[index]?.data[el]);

        return [];
      }
      return [];
    },

    generateGlobalData: (el: EPillarType): ConvertedData[] => {
      if (globalResults)
        return Object.entries(globalResults[el]).map((skill) => ({
          data: {
            score: roundAndDropTrailingZeros(skill[1]),
            blurb: `Global score is ${roundAndDropTrailingZeros(skill[1])}%`,
          },
          title: skill[0],
        }));
      return [];
    },
  };
};
